import {useContext} from 'react'

import Context from '@s-ui/react-context'
import CircleCheckOutline from '@adv-mt/mt-svg-icons/lib/CircleCheckOutline'
import CircleClose from '@adv-mt/mt-svg-icons/lib/CircleClose'

export const USER_DATA_FIELD_DEFAULT_VALUE = ''

export const USER_DATA_FIELDS = {
  NAME: 'name',
  LAST_NAME: 'lastName',
  EMAIL: 'email',
  PHONE: 'phone',
  USER_ID: 'userId',
  ZIP_CODE: 'zipCode'
}

export const useUtils = () => {
  const {domain} = useContext(Context)
  const config = domain.get('config')

  const REGEX_SPAIN_POSTALCODE = config.get('REGEX_SPAIN_POSTALCODE')
  const REGEX_VALID_PHONE_FORMAT = config.get('REGEX_PHONE_NUMBERS_AND_HACKY_PHONE_VARIATIONS')
  const regExpPhoneValidation = new RegExp(REGEX_VALID_PHONE_FORMAT)
  const regExpZipCodeValidation = new RegExp(REGEX_SPAIN_POSTALCODE)

  const getUserDataPojo = userData => {
    return {
      [USER_DATA_FIELDS.NAME]:
        userData[USER_DATA_FIELDS.NAME].value || userData[USER_DATA_FIELDS.NAME].value === USER_DATA_FIELD_DEFAULT_VALUE
          ? userData[USER_DATA_FIELDS.NAME].value
          : userData[USER_DATA_FIELDS.NAME],
      [USER_DATA_FIELDS.LAST_NAME]:
        userData[USER_DATA_FIELDS.LAST_NAME].value ||
        userData[USER_DATA_FIELDS.LAST_NAME].value === USER_DATA_FIELD_DEFAULT_VALUE
          ? userData[USER_DATA_FIELDS.LAST_NAME].value
          : userData[USER_DATA_FIELDS.LAST_NAME],
      [USER_DATA_FIELDS.EMAIL]:
        userData[USER_DATA_FIELDS.EMAIL].value ||
        userData[USER_DATA_FIELDS.EMAIL].value === USER_DATA_FIELD_DEFAULT_VALUE
          ? userData[USER_DATA_FIELDS.EMAIL].value
          : userData[USER_DATA_FIELDS.EMAIL],
      [USER_DATA_FIELDS.PHONE]:
        userData[USER_DATA_FIELDS.PHONE].value ||
        userData[USER_DATA_FIELDS.PHONE].value === USER_DATA_FIELD_DEFAULT_VALUE
          ? userData[USER_DATA_FIELDS.PHONE].value
          : userData[USER_DATA_FIELDS.PHONE],
      [USER_DATA_FIELDS.ZIP_CODE]:
        userData[USER_DATA_FIELDS.ZIP_CODE].value ||
        userData[USER_DATA_FIELDS.ZIP_CODE].value === USER_DATA_FIELD_DEFAULT_VALUE
          ? userData[USER_DATA_FIELDS.ZIP_CODE].value
          : userData[USER_DATA_FIELDS.ZIP_CODE]
    }
  }

  const checkFieldErrors = (id, value) => {
    if (id === USER_DATA_FIELDS.PHONE) return !regExpPhoneValidation.test(value)
    if (id === USER_DATA_FIELDS.ZIP_CODE) return !regExpZipCodeValidation.test(value)
    return !value
  }

  const getUserDataWithErrors = ({userData}) => {
    const userDataWithErrors = {}
    Object.keys(userData).forEach(fieldId => {
      const fieldError = checkFieldErrors(fieldId, userData[fieldId].value)

      userDataWithErrors[fieldId] = {
        ...userData[fieldId],
        error: fieldError
      }

      return fieldError
    })

    return userDataWithErrors
  }

  const getFormData = (isPurchaseFlow, userData) => {
    const defaultUserData = {
      [USER_DATA_FIELDS.NAME]: {value: userData[USER_DATA_FIELDS.NAME] || USER_DATA_FIELD_DEFAULT_VALUE, error: false},
      [USER_DATA_FIELDS.LAST_NAME]: {
        value: userData[USER_DATA_FIELDS.LAST_NAME] || USER_DATA_FIELD_DEFAULT_VALUE,
        error: false
      },
      [USER_DATA_FIELDS.EMAIL]: {
        value: userData[USER_DATA_FIELDS.EMAIL] || USER_DATA_FIELD_DEFAULT_VALUE,
        error: false
      },
      [USER_DATA_FIELDS.PHONE]: {
        value: userData[USER_DATA_FIELDS.PHONE] || USER_DATA_FIELD_DEFAULT_VALUE,
        error: false
      },
      [USER_DATA_FIELDS.USER_ID]: {
        value: userData[USER_DATA_FIELDS.USER_ID] || USER_DATA_FIELD_DEFAULT_VALUE,
        error: false
      }
    }
    return isPurchaseFlow
      ? {
          ...defaultUserData,
          [USER_DATA_FIELDS.ZIP_CODE]: {
            value: userData[USER_DATA_FIELDS.ZIP_CODE] || USER_DATA_FIELD_DEFAULT_VALUE,
            error: false
          }
        }
      : defaultUserData
  }

  const defaultFormFields = [
    {
      errorText: 'RESERVATION_C2B.USER_FORM.NAME_FIELD_ERROR_TEXT',
      id: USER_DATA_FIELDS.NAME,
      label: 'FORM.CONTACT_NAME_LABEL'
    },
    {
      errorText: 'RESERVATION_C2B.USER_FORM.LAST_NAME_FIELD_ERROR_TEXT',
      id: USER_DATA_FIELDS.LAST_NAME,
      label: 'COMMON.PLURALS.LAST_NAME'
    },
    {
      helpText: 'RESERVATION_C2B.USER_FORM.EMAIL_FIELD_HELP_TEXT',
      id: USER_DATA_FIELDS.EMAIL,
      label: 'FORM.EMAIL_LABEL'
    },
    {
      errorText: 'RESERVATION_C2B.USER_FORM.PHONE_FIELD_ERROR_TEXT',
      id: USER_DATA_FIELDS.PHONE,
      label: 'FORM.CONTACT_PHONE_LABEL'
    }
  ]

  const purchaseFormFields = [
    ...defaultFormFields,
    {
      errorText: 'RESERVATION_C2B.USER_FORM.ZIP_CODE_FIELD_ERROR_TEXT',
      id: USER_DATA_FIELDS.ZIP_CODE,
      label: 'COMMON.POSTAL_CODE'
    }
  ]

  const isPhoneOrZipCodeField = id => id === [USER_DATA_FIELDS.PHONE] || id === [USER_DATA_FIELDS.ZIP_CODE]
  const formatTimer = s => new Date(s).toISOString().substr(14, 5)

  const notificationData = {
    error: {
      Icon: CircleClose,
      text: 'RESERVATION_C2B.USER_FORM.ERROR_NOTIFICATION',
      type: 'error'
    },
    success: {
      Icon: CircleCheckOutline,
      text: 'RESERVATION_C2B.USER_FORM.SUCCESS_NOTIFICATION',
      type: 'success'
    }
  }

  return {
    formatTimer,
    getFormData,
    getUserDataWithErrors,
    getUserDataPojo,
    checkFieldErrors,
    defaultFormFields,
    purchaseFormFields,
    isPhoneOrZipCodeField,
    notificationData
  }
}
